import { AfterViewInit, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTabChangeEvent } from "@angular/material/tabs";

interface TestInfo {
  user_id: string;
  test_id: string;
  vehicle_number: string;
  master_pack_id: string;
  slave_pack_id: string;
  pkn_id: string;
  motor_serial_number: string;
  motor_controller_number: string;
}

type MetaData = {
  test_start_time: number;
  test_end_time: number;
  test_date: string;
  file_name: string;
  data: Record<
    string,
    Record<
      string,
      {
        result: string;
        params: Record<string, string>;
      }
    >
  >;
};

export interface RFInputData {
  metaData: {
    user_id: string;
    test_id: string;
    preLoad: MetaData;
    postLoad: MetaData;
    fastCharge: MetaData;
    onRoad: MetaData;
  };
  testInfo: TestInfo;
  failedCases: Record<string, Record<string, string>>;
}

@Component({
  selector: "app-metadata",
  templateUrl: "./metadata.component.html",
  styleUrls: ["./metadata.component.css"],
})
export class MetadataComponent implements OnInit, AfterViewInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RFInputData,
    private dialogRef: MatDialogRef<MetadataComponent>,
  ) {}

  testTabs = ["preLoad", "postLoad", "fastCharge", "onRoad"];
  tabLabels = {
    preLoad: "Pre-Load",
    postLoad: "Post-Load",
    fastCharge: "Fast-Charge",
    onRoad: "On-Road",
  };

  colorsData: Record<
    "majorGroup" | "minorGroup" | "param" | "failed",
    {
      background: string;
      border: string;
      headerBg: string;
      headerBorder: string;
    }
  > = {
    majorGroup: {
      background: "#E4E2E2",
      border: "#A3A3A3",
      headerBg: "#BEBEBE",
      headerBorder: "",
    },
    minorGroup: {
      background: "#F1F1F1",
      border: "#A3A3A3",
      headerBg: "#CFCFCF",
      headerBorder: "#b5b5b5",
    },
    param: {
      background: "#f1f1f1",
      border: "#a3a3a390",
      headerBg: "#c7c7c7",
      headerBorder: "",
    },
    failed: {
      background: "#ff2f3640",
      border: "#B90000",
      headerBg: "",
      headerBorder: "#B90000",
    },
  };

  getLabelByTab(tab: string) {
    return this.tabLabels[tab];
  }

  ngOnInit(): void {
    const { metaData } = this.data;
    this.testTabs = this.testTabs.filter((_) => metaData[_] !== undefined);
  }

  ngAfterViewInit(): void {
    if (this.testTabs.length != 0) {
      const testTab = this.testTabs[0];
      const metaData = this.data.metaData[testTab] as MetaData;
      const testInfoData = this.data.testInfo as TestInfo;

      this.generateMetadataUi(testTab, metaData);
      this.generateTestInfoData(testInfoData);
    }
  }

  onSelectionChanage(event: MatTabChangeEvent) {
    const { textLabel } = event.tab;
    const testTab = Object.entries(this.tabLabels).find(
      (_) => _[1] === textLabel,
    )[0];

    const metaData = this.data.metaData[testTab] as MetaData;
    this.generateMetadataUi(testTab, metaData);
  }

  generateTestInfoData(testData: TestInfo) {
    const testInfoContainer = document.getElementById("testData");

    testInfoContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      margin-top: 0.3rem;
      margin-bottom: 2rem;
    `;

    Object.entries(testData).forEach(([key, value]) => {
      // declarations
      const dataContainer = document.createElement("p");
      const keyContainer = document.createElement("span");
      const valueContainer = document.createElement("span");

      // data
      keyContainer.textContent = `${key.trim()} :`;
      valueContainer.textContent = value;

      // stylings
      dataContainer.style.cssText = `
        margin: 0;
        margin-bottom: 2px; 
      `;
      keyContainer.style.cssText = `font-weight: 400; margin-right: 0.5rem`;

      // DOM
      dataContainer.appendChild(keyContainer);
      dataContainer.appendChild(valueContainer);
      testInfoContainer.appendChild(dataContainer);
    });
  }

  generateMetadataUi(testUiId: string, metaData: MetaData) {
    const metadataContainer = document.getElementById(testUiId);

    metadataContainer.style.padding = "0.5rem";
    metadataContainer.style.paddingTop = "1.5rem";

    const data = metaData.data;

    for (const majorGroup in data) {
      const major = data[majorGroup];

      for (const minorGroup in major) {
        const item = major[minorGroup];
        item.params.result = item.result;
      }
    }

    Object.entries(metaData.data).forEach(
      ([majorGroup, majorParams], majorIndex) => {
        // declarations
        const majorGroupContainer = document.createElement("div");
        const minorGroupsContainer = document.createElement("div");

        const majorGroupNameEl = document.createElement("h2");
        const namingIndex = `${testUiId}-${majorIndex + 1}`;

        const isFailingCaseMajorGroup = Object.keys(this.data.failedCases)
          .map((_) => _.trim().toLowerCase())
          .includes(majorGroup.trim().toLowerCase());

        // data
        majorGroupNameEl.textContent = majorGroup;

        // styles
        majorGroupNameEl.id = `major-group-name-${namingIndex}`;
        majorGroupContainer.id = `major-container-${namingIndex}`;
        minorGroupsContainer.id = `minors-container-${namingIndex}`;

        majorGroupNameEl.className = `major-group-name`;
        majorGroupContainer.className = `major-container`;
        minorGroupsContainer.className = `minors-container`;

        majorGroupContainer.style.cssText = `
          background-color: ${this.colorsData.majorGroup.background};
          border: 1px solid ${this.colorsData.majorGroup.border};
          padding-bottom: 1rem;
          text-align: center; 
        `;

        majorGroupNameEl.style.cssText = `
          background-color: ${this.colorsData.majorGroup.headerBg};
          padding: 0.5rem;
          margin: 0px;
          font-size: 1.2rem;
          line-height: 2rem;
          font-weight: 500;
        `;

        // DOM
        metadataContainer.appendChild(majorGroupContainer);
        majorGroupContainer.appendChild(majorGroupNameEl);
        majorGroupContainer.appendChild(minorGroupsContainer);

        Object.entries(majorParams).forEach(
          ([minorGroup, paramData], minorIndex) => {
            // declarations
            const minorGroupContainer = document.createElement("div");
            const minorGroupParamsContainer = document.createElement("div");
            const minorGroupParamDataContainer = document.createElement("div");

            const minorGroupNameEl = document.createElement("h2");
            const minorGroupResultEl = document.createElement("span");
            const namingIndex = `${testUiId}-${majorIndex + 1}${minorIndex + 1}`;

            const isFailingCaseMinorGroup = Object.values(
              this.data.failedCases,
            ).some((_) =>
              Object.keys(_)[0]
                .trim()
                .toLowerCase()
                .includes(minorGroup.trim().toLowerCase()),
            );

            // data
            minorGroupNameEl.textContent = minorGroup;
            minorGroupResultEl.textContent = paramData.result;

            // styles
            minorGroupNameEl.id = `minor-group-name-${namingIndex}`;
            minorGroupContainer.id = `minor-container-${namingIndex}`;
            minorGroupParamsContainer.id = `minor-params-${namingIndex}`;
            minorGroupParamDataContainer.id = `minor-param-${namingIndex}`;
            minorGroupResultEl.id = `minor-result-${namingIndex}`;

            minorGroupNameEl.className = `minor-group-name`;
            minorGroupContainer.className = "minor-container";
            minorGroupParamsContainer.className = "minor-params-container";
            minorGroupParamDataContainer.className = "minor-param";
            minorGroupResultEl.className = `minor-result`;

            minorGroupContainer.style.cssText = `
              background-color: ${this.colorsData.minorGroup.background}; 
              border: 1px solid ${this.colorsData.minorGroup.border}; 
              margin: auto 0.3rem;
              margin-top: 1.5rem; 
            `;

            minorGroupNameEl.style.cssText = `
              background-color: ${this.colorsData.minorGroup.headerBg};
              border-bottom: 1px solid ${this.colorsData.minorGroup.headerBorder};
              padding: 0.2rem;
              font-size: 1rem;
              line-height: 2rem;
              font-weight: 500;
              margin-bottom: 0;
            `;

            minorGroupResultEl.style.cssText = `
              margin-left: 1rem; 
              opacity: 0.7;
            `;

            minorGroupParamsContainer.style.cssText = `
              display: grid; 
              grid-template-columns: repeat(4, 1fr); 
              gap: 0.5rem; 
              padding: 0.6rem;
            `;

            if (minorIndex === 0) {
              minorGroupContainer.style.marginTop = "0.5rem";
            }

            if (paramData.result.trim().toLowerCase().includes("fail")) {
              minorGroupNameEl.style.color = this.colorsData.failed.border;
              minorGroupNameEl.style.borderBottomColor =
                this.colorsData.failed.border;
              minorGroupNameEl.style.backgroundColor =
                this.colorsData.failed.background;
              minorGroupContainer.style.borderColor =
                this.colorsData.failed.border;
            }

            // DOM
            // minorGroupNameEl.appendChild(minorGroupResultEl);
            minorGroupContainer.appendChild(minorGroupNameEl);
            minorGroupContainer.appendChild(minorGroupParamsContainer);
            minorGroupsContainer.appendChild(minorGroupContainer);

            Object.entries(paramData.params).forEach(
              ([param, value], paramIndex) => {
                // data
                const NA = "---";
                value = value ? String(value) : NA;

                if (value !== "---") {
                  // declarations
                  const currentParamContainer = document.createElement("div");
                  const paramNameEl = document.createElement("h4");
                  const paramValueEl = document.createElement("p");
                  const namingIndex = `${testUiId}-${majorIndex + 1}${minorIndex + 1}${paramIndex + 1}`;

                  paramNameEl.textContent = param;
                  paramValueEl.textContent = String(value);

                  // styles
                  currentParamContainer.id = `param-container-${namingIndex}`;
                  paramNameEl.id = `param-name-${namingIndex}`;
                  paramValueEl.id = `param-value-${namingIndex}`;

                  currentParamContainer.className = "param-container";
                  paramNameEl.className = `param-name`;
                  paramValueEl.className = `param-value`;

                  currentParamContainer.style.cssText = `
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: space-between;
                  border: 1.2px solid ${this.colorsData.param.border};
                  background-color: ${this.colorsData.param.headerBg};
                `;

                  paramNameEl.style.cssText = `
                  font-weight: 400; 
                  font-size: 15px; 
                  padding: 0.5rem; 
                  margin: 0;
                  letter-spacing: normal;
                `;

                  paramValueEl.style.cssText = ` 
                  background-color: ${this.colorsData.param.background};
                  padding: 0.5rem;
                  margin: 0px;
                  width: 100%;
                  font-weight: 500;
                  font-size: 14px;
                `;

                  const isFailingPramValue =
                    isFailingCaseMajorGroup &&
                    isFailingCaseMinorGroup &&
                    this.data.failedCases[majorGroup][minorGroup] == value;

                  if (
                    value?.trim().toLowerCase().includes("fail") ||
                    isFailingPramValue
                  ) {
                    currentParamContainer.style.border = `1.2px solid ${this.colorsData.failed.border}`;
                    currentParamContainer.style.backgroundColor =
                      this.colorsData.failed.background;
                    paramNameEl.style.color = this.colorsData.failed.border;
                    paramNameEl.style.borderBottom =
                      this.colorsData.failed.background;
                    paramValueEl.style.color = this.colorsData.failed.border;
                  }

                  // DOM
                  currentParamContainer.appendChild(paramNameEl);
                  currentParamContainer.appendChild(paramValueEl);
                  minorGroupParamsContainer.appendChild(currentParamContainer);
                }
              },
            );
          },
        );
      },
    );
  }

  closeDailoge(): void {
    this.dialogRef.close();
  }
}
